import { Box, ClickAwayListener } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, type MouseEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK, MOTION } from "../../../theme/tokens";

/** Ties the tag to its explanation, so a screen reader announces one when the other opens. */
const TOOLTIP_ID = "resend-welcome-locked-tooltip";

/**
 * The password-link line the composer can see but never edit.
 *
 * It is shown rather than merely described because the recipient's message ends with it, and a
 * composer that hides its own last line invites someone to type their own link in. Clicking it says
 * why it can't be changed — the same answer its `title` gives on hover, for the tap that has no hover.
 */
export const LockedPasswordLine: FC<{ exampleLink: string }> = ({ exampleLink }) => {
	const { t } = useTranslation();
	const [tipOpen, setTipOpen] = useState(false);

	const closeTip = useCallback(() => setTipOpen(false), []);

	return (
		<ClickAwayListener onClickAway={closeTip}>
			<Box
				data-testid="resend-welcome-locked-line"
				title={t("team.resend_welcome.locked.title")}
				onClick={() => setTipOpen((open) => !open)}
				sx={(theme) => ({
					position: "relative",
					mt: 1.75,
					px: 1.75,
					py: 1.5,
					borderRadius: 1,
					border: `1px dashed ${alpha(theme.palette.primary.main, 0.32)}`,
					backgroundColor: alpha(theme.palette.primary.main, 0.05),
					cursor: "not-allowed",
				})}
			>
				{/* The whole block is the click target for a pointer, but a pointer is not the only way here:
				    the tag is the real control, so the explanation has a tab stop and answers to Enter. */}
				<Box sx={{ display: "flex", alignItems: "baseline", gap: 1.25, fontSize: "14px", color: INK.PRIMARY, flexWrap: "wrap" }}>
					<Box component="span" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
						{t("team.resend_welcome.locked.line", { link: exampleLink })}
					</Box>
					<Box
						component="button"
						type="button"
						data-testid="resend-welcome-locked-tag"
						aria-expanded={tipOpen}
						aria-describedby={tipOpen ? TOOLTIP_ID : undefined}
						onClick={(event: MouseEvent<HTMLButtonElement>) => {
							// The wrapper toggles on click too, and both would fire for one press.
							event.stopPropagation();
							setTipOpen((open) => !open);
						}}
						sx={{
							marginInlineStart: "auto",
							flexShrink: 0,
							fontSize: FONT_SIZE.LABEL_XS,
							color: INK.MUTED,
							textTransform: "uppercase",
							letterSpacing: "0.3px",
							border: 0,
							padding: 0,
							background: "none",
							font: "inherit",
							cursor: "help",
						}}
					>
						{t("team.resend_welcome.locked.tag")}
					</Box>
				</Box>
				<Box sx={{ mt: 0.625, fontSize: FONT_SIZE.LABEL_SM, color: INK.MUTED }}>{t("team.resend_welcome.locked.note")}</Box>
				{tipOpen && (
					<Box
						role="tooltip"
						id={TOOLTIP_ID}
						data-testid="resend-welcome-locked-tooltip"
						sx={(theme) => ({
							position: "absolute",
							insetInlineStart: 13,
							bottom: -32,
							zIndex: 8,
							px: 1.25,
							py: 0.75,
							borderRadius: 0.75,
							backgroundColor: theme.palette.primary.dark,
							color: theme.palette.primary.contrastText,
							fontSize: FONT_SIZE.CAPTION,
							animation: `resendWelcomeTipIn ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT}`,
							"@keyframes resendWelcomeTipIn": {
								from: { opacity: 0, transform: "translateY(-4px)" },
								to: { opacity: 1, transform: "translateY(0)" },
							},
							"@media (prefers-reduced-motion: reduce)": { animation: "none" },
						})}
					>
						{t("team.resend_welcome.locked.tooltip")}
					</Box>
				)}
			</Box>
		</ClickAwayListener>
	);
};
